import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { FloatingLabelInput } from '@/components/ui/floatinglable/floating';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { RootState } from '@/redux/store';
import {
    useCreateTaxGroupMutation,
    useGetSalesTaxQuery,
    useUpdateTaxGroupMutation,
} from '@/services/salesTaxApi';
import { SaleTax } from '@/types/system-setting/sale-tax';
import { Status } from '@/types/enums';
import { ErrorType, FormProps } from '@/types';
import { TaxGroup } from '@/types/system-setting/tax-group';
import { MultiSelect } from '@/components/ui/multiselect/multiselectCheckbox';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const initialValues: TaxGroup = {
    name: '',
    sale_tax_ids: [],
}

const TaxGroupForm = ({
    isOpen,
    setOpen,
    data,
    setData,
    action,
    setAction,
    refetch,
}: FormProps<TaxGroup>) => {
    const { toast } = useToast();
    const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

    const [createTaxGroup, { isLoading: createLoading }] = useCreateTaxGroupMutation();
    const [updateTaxGroup, { isLoading: updateLoading }] = useUpdateTaxGroupMutation();

    const { data: salesTaxData, isLoading: salesTaxLoading } = useGetSalesTaxQuery({ query: "status=active", org_id: orgId }, {
        skip: !open,
    });
    const salesTaxOptions = useMemo(() => {
        return Array.isArray(salesTaxData?.data)
            ? (salesTaxData?.data?.map((item: SaleTax) => ({
                value: item.id,
                label: `${item.name} (${item.percentage}%)`,
            })))
            : [];
    }, [salesTaxData]);

    const form = useForm<TaxGroup>({
        mode: "onChange",
        defaultValues: initialValues,
    });

    const {
        handleSubmit,
        reset,
        formState: { isSubmitting, errors },
    } = form;

    useEffect(() => {

        if (data && action === 'edit') {
            const payload = {
                id: data.id,
                name: data.name.trim(),
                sale_tax_ids: data.taxes?.map((item: SaleTax) => item.id),
            }
            reset(payload);
        } else {
            reset(initialValues);
        }
    }, [data, action, reset]);


    const onSubmit = async (formData: TaxGroup) => {
        const payload = {
            ...formData,
            name: formData.name.trim(),
        };

        try {
            if (action === 'add') {
                const resp = await createTaxGroup(payload);
                console.log({ resp });
                
                // Check for error first
                if (resp.error) {
                    const error = resp.error as FetchBaseQueryError & {
                        data?: { message?: string };
                    };
                    const errorMessage = ('status' in error && error.data && typeof error.data === 'object' && 'message' in error.data)
                        ? (error.data as { message?: string }).message || 'Something went wrong.'
                        : 'Something went wrong.';
                    toast({
                        variant: 'destructive',
                        title: 'Error in form Submission',
                        description: errorMessage,
                    });
                    return;
                }
                
                // Check for success
                if (resp.data) {
                    refetch();
                    toast({
                        variant: 'success',
                        title: 'Tax Group added successfully.',
                    });
                    handleClose();
                }
            } else {
                const resp = await updateTaxGroup(payload);
                console.log({ resp });
                
                // Check for error first
                if (resp.error) {
                    const error = resp.error as FetchBaseQueryError & {
                        data?: { message?: string };
                    };
                    const errorMessage = ('status' in error && error.data && typeof error.data === 'object' && 'message' in error.data)
                        ? (error.data as { message?: string }).message || 'Something went wrong.'
                        : 'Something went wrong.';
                    toast({
                        variant: 'destructive',
                        title: 'Error in form Submission',
                        description: errorMessage,
                    });
                    return;
                }
                
                // Check for success
                if (resp.data) {
                    refetch();
                    toast({
                        variant: 'success',
                        title: 'Tax Group updated successfully.',
                    });
                    handleClose();
                }
            }
        } catch (error) {
            console.error('Error', { error });
            if (error && typeof error === 'object' && 'data' in error) {
                const typedError = error as ErrorType;
                toast({
                    variant: 'destructive',
                    title: 'Error in form Submission',
                    description: `${typedError.data?.detail ||
                        (typedError.data as { message?: string }).message
                        }`,
                });
            } else {
                toast({
                    variant: 'destructive',
                    title: 'Error in form Submission',
                    description: 'Something went wrong.',
                });
            }
        }
    };

    const handleClose = () => {
        // Force reset to initial values
        reset(initialValues, {
            keepDefaultValues: false
        });
        setAction("add");
        setData(undefined);
        setOpen(false);
    };

    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent>
                <SheetHeader>
                    <SheetTitle>
                        {action === 'add' ? 'Create' : 'Edit'} Tax Group
                    </SheetTitle>
                    <SheetDescription>
                        <Separator className="h-[1px] font-thin rounded-full" />

                        <FormProvider {...form}>
                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="flex flex-col py-4 gap-4"
                            >
                                <Controller
                                    name="name"
                                    control={form.control}
                                    rules={{
                                        required: 'Required',
                                        maxLength: {
                                            value: 40,
                                            message: 'Name must be 40 characters or less',
                                        },
                                        pattern: {
                                            value: /^[a-zA-Z]+[-'s]?[a-zA-Z ]+$/,
                                            message: 'Name should contain only alphabets',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <FloatingLabelInput
                                                id="name"
                                                label="Tax Group Name"
                                                text="*"
                                                {...field}
                                            />
                                            {error && (
                                                <p className="text-xs text-red-500 mt-5 ">{error.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <Controller
                                    name="sale_tax_ids"
                                    control={form.control}
                                    rules={{
                                        required: 'Required',
                                        min: {
                                            value: 0,
                                            message: 'Minimum percentage required is 0',
                                        },
                                        max: {
                                            value: 99,
                                            message: 'Maximum percentage required is 99',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div className="space-y-1">
                                            <MultiSelect
                                                // floatingLabel={"Sales Tax"}
                                                options={
                                                    salesTaxOptions as { value: number; label: string }[]
                                                }
                                                defaultValue={field.value || []}
                                                onValueChange={(selectedValues) => {
                                                    field.onChange(selectedValues);
                                                }}
                                                placeholder={"Select Sales Tax"}
                                                variant="inverted"
                                                maxCount={1}
                                                className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 "
                                            />
                                            {error && (
                                                <p className="text-xs text-red-500 mt-5 ">{error.message}</p>
                                            )}
                                        </div>
                                    )}
                                />

                                <LoadingButton
                                    type="submit"
                                    className="bg-primary font-semibold text-black gap-1"
                                    loading={isSubmitting || createLoading || updateLoading}
                                >
                                    {!isSubmitting && !createLoading && !updateLoading && (
                                        <i className="fa-regular fa-floppy-disk text-base px-1" />
                                    )}
                                    Save
                                </LoadingButton>
                            </form>
                        </FormProvider>
                    </SheetDescription>
                </SheetHeader>
            </SheetContent>
        </Sheet>
    );
};

export default TaxGroupForm;